<template>
  <div v-if="users !== null">
    <v-row class="mx-3 my-3 align-center justify-space-between flex-wrap">
      <!-- モード切り替え -->
      <v-btn-toggle
        v-model="mode"
        mandatory
        border="md"
      >
        <v-btn flat>
          &nbsp;{{ $t('ManageAccounts.GroupsMode') }}&nbsp;
        </v-btn>
        <v-btn flat>
          &nbsp;{{ $t('ManageAccounts.ServicesMode') }}&nbsp;
        </v-btn>
      </v-btn-toggle>

      <v-spacer />

      <!-- 新規作成ボタン -->
      <v-btn
        v-if="is_admin && !selected[0]"
        flat
        class="text-secondary"
        @click="openCreateDialog()"
      >
        <v-icon
          class="mr-1"
          color="secondary"
          icon="mdi-plus"
        />
        {{ $t('Create') }}
      </v-btn>
    </v-row>

    <v-row
      v-if="!selected[0]"
      class="flex-wrap my-3 ml-3"
    >
      <!-- 検索 -->
      <SearchInput
        v-model="search"
        class="ml-3 mr-5"
        :loading="searchLoading"
      />
    </v-row>

    <!-- 選択したユーザーの所属グループを変更 -->
    <div
      v-if="selected[0] && mode===0"
      class="mt-3 mx-3"
    >
      <ChangeUsersToGroupsForm
        :select-users="selected"
        @reload="getList()"
      />
    </div>

    <!-- 選択したユーザーのサービスを変更 -->
    <div
      v-if="selected[0] && mode===1"
      class="mt-3 mx-3"
    >
      <AssignServicesToGroupsForm
        :select-users="selected"
        @reload="getList()"
      />
    </div>

    <v-data-table-server
      v-model="selected"
      :items="users"
      :headers="headers"
      item-value="id"
      :items-per-page-options="[10, 25, 50, 100]"
      :items-per-page-text="$t('DataTable.RowsPerPage')"
      :items-length="pagination.totalItems"
      :show-select="is_admin"
      @update:options="syncPagination"
    >
      <!-- header's checkbox -->
      <template #header.data-table-select="{ allSelected, selectAll, someSelected }">
        <v-checkbox-btn
          class="ml-2"
          :indeterminate="someSelected && !allSelected"
          :model-value="allSelected"
          color="secondary"
          @update:model-value="selectAll(!allSelected)"
        />
      </template>

      <template #item="{ item, isSelected, internalItem, toggleSelect }">
        <tr
          :class="{ pointer: is_admin }"
          @click="retrieve(item.id)"
        >
          <!-- checkbox -->
          <td
            v-if="is_admin"
            @click.stop
          >
            <v-checkbox-btn
              color="secondary"
              hide-details
              :model-value="isSelected(internalItem)"
              @update:model-value="toggleSelect(internalItem)"
            />
          </td>

          <!-- avatar and user name -->
          <td style="white-space: nowrap">
            <v-row class="align-center justify-start ml-0">
              <v-icon
                v-if="item.is_active === false"
                color="error"
                class="mr-2"
                icon="mdi-minus-circle"
              />
              <OpAvatar
                class="mr-2"
                :size="25"
                :profile="item"
              />
              <UserFullName :profile="item" />
            </v-row>
          </td>

          <!-- email -->
          <td style="white-space: nowrap">
            {{ item.email }}
          </td>

          <!-- authorization -->
          <td>
            <v-chip
              v-for="(v, index) in item.roles"
              :key="index"
              density="compact"
              :class="{'text-primary: white': v==='admin'}"
              :color="v === 'admin' ? 'primary' : ''"
            >
              {{ $t('User.Role.' + v) }}
            </v-chip>
          </td>

          <!-- user group/ service list -->
          <td>
            <template v-if="mode===0">
              <v-chip
                v-for="(v, index) in item.user_group_names"
                :key="index"
                density="compact"
              >
                {{ v }}
              </v-chip>
            </template>
            <template v-else-if="mode===1">
              <v-chip
                v-for="(v, index) in item.services"
                :key="index"
                density="compact"
                label
                class="my-1"
              >
                <template #prepend>
                  <img
                    :src="image(v.icon_file)"
                    height="20"
                    alt="icon"
                  >
                </template>
                {{ $t('Service.Brand.' + v.id) }}
              </v-chip>
            </template>
          </td>

          <!-- created date -->
          <td>
            {{ $filters.calendar(item.created_at_unix) }}
          </td>
        </tr>
      </template>


      <template #no-data>
        <v-layout class="text-grey justify-center">
          {{ $t('NoItems') }}
        </v-layout>
      </template>
    </v-data-table-server>

    <!-- 新規作成ダイアログ -->
    <CreateEditUserDialog
      v-if="is_admin"
      v-model="createDialog"
      :current-data="currentData"
      :selected-groups="selectedGroups"
      @reload="getList()"
    />

    <!-- 編集ダイアログ -->
    <SimpleCardDialog
      v-if="is_admin"
      v-model:dialog="editDialog"
      :max-width="'1800'"
      :title="$t('User.Users')"
    >
      <router-view />
      <template #actions>
        <ActionButton
          close
          @click="editDialog=false"
        />
      </template>
    </SimpleCardDialog>
  </div>
  <div
    v-else
    class="pa-3"
  >
    ...
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ServiceIconMixin from '../../components/parts/mixins/ServiceIconMixin'
import CreateEditUserDialog from '../../components/dialogs/CreateEditUserDialog'
import DrfSyncDataTableMixin from './mixins/DrfSyncDataTableMixin'
import SimpleCardDialog from '../../components/parts/dialog/SimpleCardDialog'
import ActionButton from '../../components/parts/button/ActionButton'
import SearchInput from '../../components/parts/inputs/SearchInput'
import ChangeUsersToGroupsForm
  from '../../components/forms/ChangeUsersToGroupsForm'
import AssignServicesToGroupsForm
  from '../../components/forms/AssignServicesToGroupsForm'
import OpAvatar from '../../components/parts/OpAvatar'
import UserFullName from '../../components/parts/UserFullName'

export default {
  components: {
    UserFullName,
    OpAvatar,
    AssignServicesToGroupsForm,
    ChangeUsersToGroupsForm,
    SearchInput,
    ActionButton,
    SimpleCardDialog,
    CreateEditUserDialog
  },
  mixins: [ServiceIconMixin, DrfSyncDataTableMixin],
  props: {
    selectedGroups: {
        type: Array,
        default: null
      }
  },
  data () {
    return {
      users: null,
      createDialog: false,
      editDialog: false,
      selected: [],
      currentData: null,
      loadingList: false,
      mode: 0
    }
  },
  computed: {
    ...mapGetters(['ui', 'login']),
    searchLoading () {
      return this.loadingList && this.search !== null
    },
    is_admin () {
      return Boolean(this.login.profile.is_admin)
    },
    headers () {
      let headers = [
        {
          title: this.$t('User.UserName'),
          sortable: true,
          key: 'first_name_en'
        },
        { title: this.$t('User.EmailAddress'), sortable: true, key: 'email' },
        { title: this.$t('User.Role.Roles'), sortable: false, key: 'roles' },
        { title: this.$t('UserGroup.UserGroups'), sortable: false, key: 'user_group_names' },
        { title: this.$t('User.Created'), sortable: true, key: 'created_at_unix' }
      ]
      if (this.mode === 0) {
        headers[3] = {
          title: this.$t('UserGroup.UserGroups'),
          sortable: false,
          key: 'user_group_names'
        }
        return headers
      } else {
        headers[3] = {
          title: this.$t('Service.AssignedServices'),
          sortable: false,
          key: 'services'
        }
        return headers
      }
    }
  },
  watch: {
    '$route' (to, from) {
      this.openCloseEditDialog()
    },
    editDialog () {
      if (this.editDialog === false) {
        this.$router.push({ path: '/manage-accounts' })
        this.getList()
      }
    },
    'selectedGroups' () { this.getListWatchDebounce() }
  },
  created () {
    this.pagination = { descending: true, page: 1, rowsPerPage: 10, sortBy: 'created_at' }
    this.getList()
  },
  mounted () {
    setTimeout(() => {
      this.openCloseEditDialog()
    }, 3000)
  },
  methods: {
    getList () {
      let url = null
      url = 'users/'

      this.loadingList = true
      let params = this.getSyncParams()
      params.user_groups = this.selectedGroups
      this.$http(url, { params: params }).then((res) => {
        this.users = res.data.results
        this.users.forEach((user) => {
          user.services = this.$filters.removeUnusedServices(user.services)
        })
        this.applySyncData(res)
        this.loadingList = false
      })
    },
    retrieve (id) {
      if (this.is_admin) {
        this.$router.push({ path: `/manage-accounts/${id}` })
      }
    },
    openCreateDialog () {
      setTimeout(() => {
        this.currentData = null
      })
      this.createDialog = true
    },
    openCloseEditDialog () {
      this.editDialog = Boolean(this.$route.params.id)
    }
  }
}
</script>
